import React, { useState } from "react";
import { toast } from "sonner";
import { Eye, FileBadge2, Plus, Printer } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAction, useData } from "@/hooks/useData";
import { Button, DataTable, Drawer, Field, Input, Loader, PageHeader, PersonCell, Select, StatusBadge, TableCard } from "@/components/ui";
import { openCertificate } from "@/lib/documents";
import { aed, dateLabel } from "@/lib/format";

const PURPOSES = [
  { value: "Bank", label: "Bank" }, { value: "Embassy", label: "Embassy" },
  { value: "Visa", label: "Visa" }, { value: "Personal", label: "Personal use" },
];

const open = (cert, print) => { if (!openCertificate(cert, print)) toast.error("Allow pop-ups to view the certificate"); };

export default function SalaryCertificates() {
  const { user } = useAuth();
  const isEmployee = user.role === "employee";
  const { data: list, reload } = useData(() => api.certificates.list(user), [user]);
  const { data: employees } = useData(() => (isEmployee ? Promise.resolve([]) : api.employees.list(user)), [user]);
  const { run, pending } = useAction();

  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState({ employee_id: isEmployee ? user.employee_id : "", purpose: "Bank", addressed_to: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    const { ok } = await run(() => api.certificates.request(form), "Certificate generated");
    if (ok) {
      setDrawer(false);
      setForm((f) => ({ ...f, addressed_to: "" }));
      reload();
    }
  };

  const columns = [
    ...(!isEmployee ? [{ key: "employee", header: "Employee", render: (c) => <PersonCell name={c.employee_name} sub={c.designation} /> }] : []),
    { key: "purpose", header: "Purpose", render: (c) => <span className="text-xs font-bold text-brand-blue">{c.purpose}</span> },
    { key: "to", header: "Addressed to", hide: "md", className: "max-w-[240px]", render: (c) => <p className="text-xs text-slate-500 truncate" title={c.addressed_to}>{c.addressed_to}</p> },
    { key: "salary", header: "Monthly (AED)", align: "right", hide: "lg", render: (c) => <span className="text-xs font-extrabold text-brand-blue tabular-nums">{aed(c.monthly_salary, 0)}</span> },
    { key: "issued", header: "Issued", hide: "lg", render: (c) => <span className="text-xs">{dateLabel(c.created_at)}</span> },
    { key: "status", header: "Status", hide: "md", render: (c) => <StatusBadge status={c.status} /> },
    { key: "actions", header: "", align: "right", render: (c) => (
      <div className="flex gap-1.5 justify-end">
        <Button size="icon" variant="ghost" onClick={() => open(c, false)} aria-label="View certificate" title="View"><Eye size={14} /></Button>
        <Button size="icon" variant="ghost" onClick={() => open(c, true)} aria-label="Print or save as PDF" title="Print / PDF"><Printer size={14} /></Button>
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader
        description="UAE-format salary certificates for banks, embassies, visas or personal use. Certificates are issued instantly."
        actions={<Button variant="primary" icon={<Plus size={14} />} onClick={() => setDrawer(true)}>Request certificate</Button>}
      />

      {!list ? <Loader /> : (
        <TableCard heading={`${list.length} certificate${list.length === 1 ? "" : "s"}`}>
          <DataTable columns={columns} rows={list} empty={{ icon: FileBadge2, title: "No certificates yet", message: "Request one to get started." }} />
        </TableCard>
      )}

      <Drawer
        open={drawer}
        onClose={() => setDrawer(false)}
        title="Request salary certificate"
        subtitle="Generated from the employee's current contract"
        footer={<>
          <Button onClick={() => setDrawer(false)}>Cancel</Button>
          <Button variant="primary" loading={pending} disabled={!form.employee_id || !form.addressed_to.trim()} onClick={submit}>Generate</Button>
        </>}
      >
        <form onSubmit={submit} className="space-y-4">
          {!isEmployee && (
            <Field label="Employee">
              <Select value={form.employee_id} onChange={set("employee_id")} placeholder="Select employee" options={(employees || []).map((e) => ({ value: e.id, label: e.name }))} />
            </Field>
          )}
          <Field label="Purpose"><Select value={form.purpose} onChange={set("purpose")} options={PURPOSES} /></Field>
          <Field label="Addressed to"><Input value={form.addressed_to} onChange={set("addressed_to")} placeholder="e.g. Emirates NBD — Credit Card" /></Field>
        </form>
      </Drawer>
    </>
  );
}
